import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { storageHelpers } from '@utils'
import { pubSubEventHandler } from '@services'
import { PUBSUB_GROUP_EVENT, ROUTES } from '@common'

import { useCartRedux } from './useRedux'
import { useCashRegister } from './useCashRegister'

export const usePaymentEvents = () => {
    const navigate = useNavigate()
    const { assistantCarts, clearAssistantData } = useCartRedux()
    const { joinedGroupName } = useCashRegister()

    // Payment event state
    const [paymentEvent, setPaymentEvent] = useState({
        isVisible: false,
        eventType: null,
        eventData: {},
    })

    // clear payment event data when new cart item is added (for closing modal)
    useEffect(() => {
        if (assistantCarts) {
            console.log('Assistant carts updated', assistantCarts)
            if (assistantCarts[joinedGroupName]?.items?.length > 0) {
                setPaymentEvent({
                    isVisible: false,
                    eventType: null,
                    eventData: {},
                })
            }
        }
    }, [assistantCarts])

    // Handle payment events
    const handlePaymentEvent = useCallback((eventType, eventData) => {
        setPaymentEvent({
            isVisible: true,
            eventType,
            eventData,
        })
    }, [])

    const handlePaymentEventClose = useCallback(() => {
        setPaymentEvent({
            isVisible: false,
            eventType: null,
            eventData: {},
        })
        clearAssistantData()
    }, [clearAssistantData])

    const handlePaymentFailed = useCallback(() => {
        setPaymentEvent({
            isVisible: true,
            eventType: PUBSUB_GROUP_EVENT.PAYMENT_FAILED,
            eventData: {},
        })
    }, [])

    const handlePaymentRequest = useCallback(
        payload => {
            handlePaymentEventClose()
            if (payload?.meta?.paymentUrl) {
                // Use localStorage for better persistence across redirects.
                storageHelpers.setPaymentOrigin('checkout-assistant')
                storageHelpers.setCheckoutAssistantPaymentSuccess({
                    number: payload.number,
                    timestamp: Date.now(),
                })

                // Parse the paymentUrl to extract parameters
                // Expected format: /order/:orderId/payment/:paymentIntentId/:clientSecret
                const url = new URL(payload.meta.paymentUrl, window.location.origin)
                const pathParts = url.pathname.split('/')

                // Extract parameters from URL path
                const orderIdIndex = pathParts.indexOf('order') + 1
                const paymentIndex = pathParts.indexOf('payment') + 1

                if (orderIdIndex > 0 && paymentIndex > 0 && pathParts.length >= paymentIndex + 2) {
                    const orderId = pathParts[orderIdIndex]
                    const paymentIntentId = pathParts[paymentIndex]
                    const clientSecret = pathParts[paymentIndex + 1]

                    navigate(
                        ROUTES.Payment.root
                            .replace(':orderId', orderId)
                            .replace(':paymentIntentId', paymentIntentId)
                            .replace(':clientSecret', clientSecret)
                    )
                } else {
                    // Fallback to original URL if parsing fails
                    console.warn('Failed to parse paymentUrl, using original URL:', payload.meta.paymentUrl)
                    window.location.href = payload.meta.paymentUrl
                }
            } else {
                console.error('Payment URL not found in payload', payload)
            }
        },
        [handlePaymentEventClose, navigate]
    )

    // Listen for payment events from PubSub
    useEffect(() => {
        const removePaymentLaterListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_LATER, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_LATER, payload)
        })

        const removePaymentRequestListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_REQUEST, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_REQUEST, payload)
        })

        const removePaymentSuccessListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS, payload)
        })

        const removePaymentFailedListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_FAILED, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_FAILED, payload)
        })

        const removePaymentCancelListener = pubSubEventHandler.addEventListener(PUBSUB_GROUP_EVENT.PAYMENT_CANCEL, payload => {
            handlePaymentEvent(PUBSUB_GROUP_EVENT.PAYMENT_CANCEL, payload)
        })

        return () => {
            removePaymentLaterListener()
            removePaymentRequestListener()
            removePaymentSuccessListener()
            removePaymentFailedListener()
            removePaymentCancelListener()
        }
    }, [handlePaymentEvent])

    // Clear assistant data when returning from payment - only run once on mount
    useEffect(() => {
        clearAssistantData()

        // Check if returning from checkout-assistant payment success
        const checkoutAssistantPaymentSuccess = storageHelpers.getCheckoutAssistantPaymentSuccess()
        if (checkoutAssistantPaymentSuccess) {
            try {
                const paymentData = checkoutAssistantPaymentSuccess
                // Remove the flag immediately to prevent showing again
                storageHelpers.clearCheckoutAssistantPaymentSuccess()

                // Show payment success popup with order ID
                setPaymentEvent({
                    isVisible: true,
                    eventType: PUBSUB_GROUP_EVENT.PAYMENT_SUCCESS,
                    eventData: paymentData,
                })
            } catch (error) {
                console.error('Failed to parse Checkout Assistant payment success data:', error)
                storageHelpers.clearCheckoutAssistantPaymentSuccess()
            }
        }
    }, [])

    return {
        paymentEvent,
        handlePaymentEventClose,
        handlePaymentRequest,
        handlePaymentFailed,
    }
}
